import { getCurrentInstance } from 'virtual-vue';

export type ObjectEmitsOptions = Record<
string,
((...args: any[]) => any) | null
>;

export type EmitsOptions = ObjectEmitsOptions | string[];

export type UnionToIntersection<U> = (
  U extends any ? (k: U) => void : never
) extends (k: infer I) => void
  ? I
  : never;

export type EmitFn<
  Options = ObjectEmitsOptions,
  Event extends keyof Options = keyof Options,
> =
  Options extends Array<infer V>
    ? (event: V, ...args: any[]) => void
    : {} extends Options
      ? (event: string, ...args: any[]) => void
      : UnionToIntersection<
      {
        [key in Event]: Options[key] extends (...args: infer Args) => any
          ? (event: key, ...args: Args) => void
          : Options[key] extends any[]
            ? (event: key, ...args: Options[key]) => void
            : (event: key, ...args: any[]) => void
      }[Event]
      >;

export type ComponentTypeEmits = ((...args: any[]) => any) | Record<string, any>;

type RecordToUnion<T extends Record<string, any>> = T[keyof T];

type ShortEmits<T extends Record<string, any>> = UnionToIntersection<
RecordToUnion<{
  [K in keyof T]: (evt: K, ...args: T[K]) => void
}>
>;

/**
 * 用于声明与发送组件的事件。预期参数与组件的 emit 选项相同。
 *
 * Example runtime declaration:
 * ```js
 * const emit = useEmits(['change', 'update'])
 * ```
 *
 * Example type-based declaration 1:
 * ```ts
 * interface EmitEvents {
 *  (event: 'change'): void;
 *  (event: 'update', value: number)
 * }
 *
 * const emit = useEmits<EmitEvents>()
 *
 * emit('change')
 * emit('update', 1)
 * ```
 * Example type-based declaration 2:
 * ```ts
 * const emit = useEmits<{
 *  // <eventName>: <expected arguments>
 *  change: []
 *  update: [value: number] // named tuple syntax
 * }>()
 *
 * emit('change')
 * emit('update', 1)
 * ```
 * 注：
 *
 * 1、类型上和使用上对标「defineEmits」宏：https://github.com/vuejs/core/blob/201936f9a3909ae9dca4e131e1f5ad5a1c0feb77/packages/runtime-core/src/apiSetupHelpers.ts#L133
 *
 * 2、使用推荐只在setup最顶层使用
 * @see {@link https://vuejs.org/api/sfc-script-setup.html#defineprops-defineemits}
 * @param emitOptions
 */
export function useEmits<EE extends string = string>(
  emitOptions: EE[],
): EmitFn<EE[]>;
export function useEmits<E extends EmitsOptions = EmitsOptions>(
  emitOptions: E,
): EmitFn<E>;
export function useEmits<T extends ComponentTypeEmits>(): T extends (
  ...args: any[]
) => any
  ? T
  : ShortEmits<T>;
export function useEmits() {
  const instance = getCurrentInstance();
  return (event, ...args) => {
    // issue：https://github.com/vuejs/vue/issues/12710
    // 注意：此处官方建议从proxy读，不要直接从instance读取（getCurrentInstance 返回的是 internal instance，除了 proxy 外（等同于 public instance）其它属性都视为私有属性，想要保持一致应该避免使用除 proxy 外的其它属性。）
    if (instance?.proxy?.$emit) {
      return instance?.proxy?.$emit(event, ...args);
    }
    throw new Error('useEmits获取emit方法失败，请检查useEmits方法使用是否正确正常');
  };
}
